import React, { createContext, useContext, useRef, useCallback, useEffect, useState } from 'react';

export type InteractionType = 'mouse' | 'keyboard' | 'touch' | 'scroll' | 'focus';

export type InteractionEvent = {
  type: InteractionType;
  timestamp: number;
  event: Event;
};

type InteractionContextValue = {
  hasInteracted: boolean;
  firstInteraction: InteractionEvent | null;
  subscribe: (callback: (interaction: InteractionEvent) => void) => () => void;
};

const InteractionContext = createContext<InteractionContextValue | null>(null);

type InteractionProviderProps = {
  children: React.ReactNode;
};

export function InteractionProvider({ children }: InteractionProviderProps) {
  const [hasInteracted, setHasInteracted] = useState(false);
  const [firstInteraction, setFirstInteraction] = useState<InteractionEvent | null>(null);
  const controllerRef = useRef<AbortController>();
  const subscribersRef = useRef<Set<(interaction: InteractionEvent) => void>>(new Set());

  const createInteractionEvent = useCallback((type: InteractionType, event: Event): InteractionEvent => {
    return {
      type,
      timestamp: Date.now(),
      event,
    };
  }, []);

  const handleFirstInteraction = useCallback(
    (type: InteractionType, event: Event) => {
      if (hasInteracted) return; // Only handle the first interaction

      const interaction = createInteractionEvent(type, event);

      setHasInteracted(true);
      setFirstInteraction(interaction);

      // Notify all subscribers
      subscribersRef.current.forEach((callback) => {
        try {
          callback(interaction);
        } catch (error) {
          console.error('Error in interaction callback:', error);
        }
      });

      // Abort all event listeners since we only care about the first interaction
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    },
    [hasInteracted, createInteractionEvent],
  );

  const subscribe = useCallback(
    (callback: (interaction: InteractionEvent) => void) => {
      subscribersRef.current.add(callback);

      // Set up event listeners on first subscription
      if (subscribersRef.current.size === 1 && !controllerRef.current) {
        const controller = new AbortController();
        controllerRef.current = controller;
        const { signal } = controller;

        // Mouse events - mousemove and mousedown for comprehensive detection
        document.addEventListener(
          'mousemove',
          (event) => {
            handleFirstInteraction('mouse', event);
          },
          { signal },
        );

        document.addEventListener(
          'mousedown',
          (event) => {
            handleFirstInteraction('mouse', event);
          },
          { signal },
        );

        // Keyboard events - only keydown needed for first interaction
        document.addEventListener(
          'keydown',
          (event) => {
            handleFirstInteraction('keyboard', event);
          },
          { signal },
        );

        // Touch events - only touchstart needed for first interaction
        document.addEventListener(
          'touchstart',
          (event) => {
            handleFirstInteraction('touch', event);
          },
          { signal },
        );

        // Scroll events - only scroll needed for first interaction
        document.addEventListener(
          'scroll',
          (event) => {
            handleFirstInteraction('scroll', event);
          },
          { signal },
        );

        // Focus events - only focus needed for first interaction
        document.addEventListener(
          'focus',
          (event) => {
            handleFirstInteraction('focus', event);
          },
          { signal },
        );
      }

      // Return unsubscribe function
      return () => {
        subscribersRef.current.delete(callback);

        // Clean up event listeners when no more subscribers
        if (subscribersRef.current.size === 0 && controllerRef.current) {
          controllerRef.current.abort();
          controllerRef.current = undefined;
        }
      };
    },
    [handleFirstInteraction],
  );

  // Expose subscription function to window for external scripts
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.subscribeToUserInteraction = (callback: (interaction: InteractionEvent) => void) => {
        // If user has already interacted, call the callback immediately
        if (hasInteracted && firstInteraction) {
          callback(firstInteraction);
          return () => {}; // Return empty unsubscribe function
        }

        // Otherwise, subscribe to future interactions
        return subscribe(callback);
      };
    }
  }, [hasInteracted, firstInteraction, subscribe]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  const contextValue: InteractionContextValue = {
    hasInteracted,
    firstInteraction,
    subscribe,
  };

  return <InteractionContext.Provider value={contextValue}>{children}</InteractionContext.Provider>;
}

function useInteractionContext(): InteractionContextValue {
  const context = useContext(InteractionContext);
  if (!context) {
    throw new Error('useInteractionContext must be used within an InteractionProvider');
  }
  return context;
}

// Hook that uses the context
type UseFirstInteractionOptions = {
  /** Callback fired when first interaction is detected */
  onFirstInteraction?: (interaction: InteractionEvent) => void;
};

type UseFirstInteractionReturn = {
  /** Whether the user has interacted at least once */
  hasInteracted: boolean;
  /** The first interaction event */
  firstInteraction: InteractionEvent | null;
};

export function useFirstInteraction({ onFirstInteraction }: UseFirstInteractionOptions = {}): UseFirstInteractionReturn {
  const { hasInteracted, firstInteraction, subscribe } = useInteractionContext();

  useEffect(() => {
    // Always subscribe to ensure event listeners are set up and state is current
    const unsubscribe = subscribe((interaction) => {
      // Only call the callback if provided
      onFirstInteraction?.(interaction);
    });

    // Cleanup subscription on unmount
    return unsubscribe;
  }, [onFirstInteraction, subscribe]);

  return {
    hasInteracted,
    firstInteraction,
  };
}
